import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type Ref } from 'react';
import { Button, Input, type ButtonProps } from '@cloudscape-design/components';
import { useAuth, useBreadcrumbs } from '@deloitte-us-consulting/gp-sdk';
import { TOOLS, type ToolGroupId } from '../../links/growthPlatformsLinks';
import type { PluginEntry } from '../../remotes/registry';
import { useRegisteredApps } from '../../remotes/useRegisteredApps';
import { useLocalUsage, useTrackAccess, useTrackClick } from '../../usage/track';
import { CornerTicks } from '../../theme/frontDoor/CornerTicks';
import { FdLink } from '../../theme/frontDoor/FdLink';
import { CONTACT_EMAIL } from '../contact';
import { AppTile, AppTileSkeleton, NextAppTile, categoryStyle } from './AppTile';
import HeroPanelButton from './HeroPanelButton';
import { appIcon } from './appIcons';
import { APP_CATEGORIES, categoryById } from './appStoreVocabulary';
import { LAUNCHER_COPY as C } from './launcherCopy';
import {
  categoriesInUse,
  categoryCounts,
  filterApps,
  filterByCategory,
  filterTools,
  firstName,
  greeting,
  monogram,
  type CategoryFilter,
} from './launcherModel';
import { topQuickLaunchItems, type QuickLaunchItem } from './quickLaunch';
import { ToolGroupPanel } from './ToolsSection';
import '../../theme/frontDoor/tokens.css';
import '../../theme/frontDoor/frontDoorDark.css';
import './launcher.css';

const SKELETON_COUNT = 6;
const GRID_TITLE_ID = 'gp-fd-apps-title';

type LauncherTabId = 'apps' | 'growth-platforms-tools' | 'all-mbrs';

/**
 * A plain, hand-rolled tablist (not Cloudscape's `Tabs`) so the launcher keeps the
 * frontDoor skin's own look (Arial, the green accent, the paper/canvas palette)
 * instead of picking up Cloudscape's visual-refresh styling, which reads as a
 * different, more generic product next to the rest of this page.
 */
const TABS: readonly { id: LauncherTabId; label: string }[] = [
  { id: 'apps', label: C.appsTab },
  { id: 'growth-platforms-tools', label: C.toolsTab },
  { id: 'all-mbrs', label: C.allMbrsTab },
];

/** The TOOL_GROUPS entry each tools tab shows. */
const TOOL_GROUP_OF_TAB: Record<Exclude<LauncherTabId, 'apps'>, ToolGroupId> = {
  'growth-platforms-tools': 'growth-platforms',
  'all-mbrs': 'all-mbrs',
};

/**
 * The Growth Platforms App Store launcher at `/` (ADR 0028).
 *
 * It reads only the registry list the side nav already loaded (useRegisteredApps is
 * memoized), so the first screen after sign-in has no failure mode of its own. A
 * registry outage says the apps could not load, never "no apps are available to
 * you", which would send people to request access they already have.
 */
export default function Launcher() {
  const { user } = useAuth();
  useTrackAccess('home');
  const { apps, loading, error, retry } = useRegisteredApps();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryFilter>('all');
  const [activeTab, setActiveTab] = useState<LauncherTabId>('apps');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  // Retry swaps its own button for skeleton tiles, so park focus on the grid
  // heading while the retry runs, and hand it back to Retry if that fails too.
  const gridTitleRef = useRef<HTMLHeadingElement>(null);
  const retryButtonRef = useRef<ButtonProps.Ref>(null);
  const retryPending = useRef(false);
  const inUse = categoriesInUse(apps);
  // The chosen chip can outlive its category when the app list changes, so fall
  // back to All rather than filter by a category no chip shows.
  const active: CategoryFilter = category !== 'all' && inUse.includes(category) ? category : 'all';
  // Search first, then category: the chip counts describe the searched list.
  const searched = filterApps(apps, query);
  const tools = filterTools(TOOLS, query);
  const shown = filterByCategory(searched, active);
  const counts = categoryCounts(searched);
  const categoryLabel = active === 'all' ? undefined : categoryById(active)?.label;
  const showChips = !loading && !error && inUse.length > 0;
  const clear = () => {
    setQuery('');
    setCategory('all');
  };
  const onRetry = () => {
    retryPending.current = true;
    gridTitleRef.current?.focus();
    retry();
  };

  useEffect(() => {
    if (!retryPending.current || loading) return;
    retryPending.current = false;
    if (error) retryButtonRef.current?.focus();
  }, [loading, error]);

  // Roving tabindex (WAI-ARIA Tabs pattern): arrow keys move focus AND selection
  // between tabs; Home/End jump to the ends. Tab itself still leaves the tablist.
  const selectTabAt = (index: number) => {
    const next = TABS[index];
    if (!next) return;
    setActiveTab(next.id);
    tabRefs.current[index]?.focus();
  };
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      selectTabAt((index + 1) % TABS.length);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      selectTabAt((index - 1 + TABS.length) % TABS.length);
    } else if (e.key === 'Home') {
      e.preventDefault();
      selectTabAt(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      selectTabAt(TABS.length - 1);
    }
  };

  // The SDK's useBreadcrumbs has no cleanup, so the previous page's crumbs and tab
  // title would otherwise linger here. Clear the crumbs and set our own title.
  useBreadcrumbs([]);
  useEffect(() => {
    document.title = C.documentTitle;
  }, []);

  return (
    <div className="gp-fd gp-fd-launcher">
      <section className="gp-fd-canvas gp-fd-launcher-hero">
        <span className="gp-fd-ring gp-fd-ring-a" aria-hidden="true" />
        <span className="gp-fd-ring gp-fd-ring-b" aria-hidden="true" />
        <HeroPanelButton panel="menu" />
        <HeroPanelButton panel="links" />
        <div className="gp-fd-inner gp-fd-launcher-hero-grid gp-fd-rise">
          <div className="gp-fd-launcher-hero-copy">
            <p className="gp-fd-eyebrow">
              <span aria-hidden="true" />
              {C.eyebrow}
            </p>
            <h1 className="gp-fd-launcher-title">{greeting(new Date(), firstName(user?.name))}</h1>
            <p className="gp-fd-lede">{C.lede}</p>
            <div className="gp-fd-launcher-controls">
              <div className="gp-fd-search">
                <Input
                  type="search"
                  value={query}
                  placeholder={C.searchPlaceholder}
                  ariaLabel={C.searchLabel}
                  clearAriaLabel={C.clearInput}
                  onChange={({ detail }) => setQuery(detail.value)}
                />
              </div>
              <FdLink to="/about" className="gp-fd-button gp-fd-button-ghost">
                {C.aboutCta}
              </FdLink>
            </div>
          </div>
          <HeroQuickDock apps={apps} loading={loading} />
        </div>
      </section>

      <section className="gp-fd-paper">
        <div className="gp-fd-inner gp-fd-rise gp-fd-rise-2">
          {/* One live region, mounted from the start regardless of the active tab, so
              screen readers pick up loading, error, and search-result changes. */}
          <p role="status" className="gp-fd-sr-only">
            {announcement({ loading, error, query, count: shown.length, categoryLabel, toolCount: tools.length })}
          </p>
          <div className="gp-fd-hud-bar">
            <div className="gp-fd-tablist" role="tablist" aria-label={C.tabsLabel}>
              {TABS.map((t, i) => (
                <button
                  key={t.id}
                  ref={(el) => {
                    tabRefs.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`gp-fd-tab-${t.id}`}
                  aria-selected={activeTab === t.id}
                  aria-controls={`gp-fd-tabpanel-${t.id}`}
                  tabIndex={activeTab === t.id ? 0 : -1}
                  className="gp-fd-tab"
                  onClick={() => setActiveTab(t.id)}
                  onKeyDown={(e) => onTabKeyDown(e, i)}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div
            role="tabpanel"
            id={`gp-fd-tabpanel-${activeTab}`}
            aria-labelledby={`gp-fd-tab-${activeTab}`}
            className="gp-fd-tabpanel"
          >
            {activeTab === 'apps' && (
              <div aria-labelledby={GRID_TITLE_ID}>
                <p className="gp-fd-kicker">{loading || error ? C.gridKicker : `${C.gridKicker} · ${shown.length}`}</p>
                <h2 id={GRID_TITLE_ID} ref={gridTitleRef} tabIndex={-1} className="gp-fd-section-title">
                  {C.gridTitle}
                </h2>
                {showChips && (
                  <div className="gp-fd-chips" role="group" aria-label={C.filterGroupLabel}>
                    <CategoryChip
                      label={C.allChip}
                      count={counts.all}
                      pressed={active === 'all'}
                      onSelect={() => setCategory('all')}
                    />
                    {APP_CATEGORIES.filter((c) => inUse.includes(c.id)).map((c) => (
                      <CategoryChip
                        key={c.id}
                        label={c.label}
                        dot={c.bar}
                        count={counts[c.id]}
                        pressed={active === c.id}
                        onSelect={() => setCategory(c.id)}
                      />
                    ))}
                  </div>
                )}
                {renderApps({ loading, error, apps, shown, query, categoryLabel, clear, onRetry, retryButtonRef })}
              </div>
            )}
            {activeTab !== 'apps' && <ToolGroupPanel tools={tools} groupId={TOOL_GROUP_OF_TAB[activeTab]} />}
          </div>
        </div>
      </section>

      <section className="gp-fd-canvas gp-fd-strip">
        <div className="gp-fd-inner">
          <div>
            <p className="gp-fd-kicker">{C.stripKicker}</p>
            <h2 className="gp-fd-strip-title">{C.stripTitle}</h2>
          </div>
          <div className="gp-fd-actions">
            <FdLink to="/onboard" className="gp-fd-button gp-fd-button-primary">
              {C.stripPrimary}
            </FdLink>
            <a href={`mailto:${CONTACT_EMAIL}`} className="gp-fd-button gp-fd-button-ghost">
              {C.stripSecondary}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

interface AnnouncementState {
  loading: boolean;
  error: boolean;
  query: string;
  count: number;
  categoryLabel: string | undefined;
  toolCount: number;
}

const appCount = (count: number) => `${count} ${count === 1 ? C.appOne : C.appMany}`;

/**
 * The live region's text: the apps sentence, then, while a search is active, a
 * tools sentence — even while loading, since the tools section is already
 * filtered and on screen (spec §4.3). With no search the tools aren't
 * filtered, so they aren't announced.
 */
function announcement(s: AnnouncementState): string {
  const apps = appsAnnouncement(s);
  if (!s.query.trim()) return apps;
  const tools = toolsAnnouncement(s.toolCount);
  return apps ? `${sentence(apps)} ${tools}` : tools;
}

/** Ends `text` with a period unless it already ends a sentence. */
function sentence(text: string): string {
  return /[.!?]$/.test(text) ? text : `${text}.`;
}

function toolsAnnouncement(count: number): string {
  if (count === 0) return C.toolsMatchNone;
  return count === 1 ? C.toolsMatchOne : `${count} ${C.toolsMatchMany}`;
}

/** What the live region says: loading, error, or the result count when searching. */
function appsAnnouncement({ loading, error, query, count, categoryLabel }: AnnouncementState): string {
  if (loading) return C.loading;
  if (error) return `${C.errorTitle} ${C.errorBody}`;
  const searching = !!query.trim();
  if (!searching) return categoryLabel ? `${appCount(count)} ${C.inCategory} ${categoryLabel}` : '';
  const matches = count === 1 ? C.matchOne : `${count} ${C.matchMany}`;
  return categoryLabel ? `${matches} ${C.inCategory} ${categoryLabel}` : matches;
}

interface ChipProps {
  label: string;
  count: number;
  pressed: boolean;
  onSelect: () => void;
  dot?: string | undefined;
}

/** A category filter toggle. Its aria-label reads "Insights & reporting, 2 apps". */
function CategoryChip({ label, count, pressed, onSelect, dot }: ChipProps) {
  return (
    <button
      type="button"
      className="gp-fd-chip"
      aria-pressed={pressed}
      aria-label={`${label}, ${appCount(count)}`}
      onClick={onSelect}
    >
      {dot && <span className="gp-fd-chip-dot" style={{ background: dot }} aria-hidden="true" />}
      <span>{label}</span>
      <span className="gp-fd-chip-count" aria-hidden="true">
        {count}
      </span>
    </button>
  );
}

interface AppsState {
  loading: boolean;
  error: boolean;
  apps: PluginEntry[];
  shown: PluginEntry[];
  query: string;
  categoryLabel: string | undefined;
  clear: () => void;
  onRetry: () => void;
  retryButtonRef: Ref<ButtonProps.Ref>;
}

function renderApps({
  loading,
  error,
  apps,
  shown,
  query,
  categoryLabel,
  clear,
  onRetry,
  retryButtonRef,
}: AppsState): ReactNode {
  if (loading) {
    return (
      <div className="gp-fd-grid">
        {Array.from({ length: SKELETON_COUNT }, (_, i) => (
          <AppTileSkeleton key={i} />
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="gp-fd-empty">
        <p className="gp-fd-empty-title">{C.errorTitle}</p>
        <p className="gp-fd-empty-body">{C.errorBody}</p>
        <Button ref={retryButtonRef} onClick={onRetry}>
          {C.retry}
        </Button>
      </div>
    );
  }
  if (apps.length === 0) {
    return (
      <div className="gp-fd-empty">
        <p className="gp-fd-empty-title">{C.emptyTitle}</p>
        <p className="gp-fd-empty-body">{C.emptyBody}</p>
        <div className="gp-fd-empty-actions">
          <FdLink to="/onboard" className="gp-fd-link">
            {C.emptyBuild}
          </FdLink>
          <FdLink to="/access-requests" className="gp-fd-link">
            {C.emptyAccess}
          </FdLink>
        </div>
      </div>
    );
  }
  if (shown.length === 0) {
    const q = query.trim();
    const lead = q ? `${C.noMatchPrefix} "${q}"` : C.noAppsPrefix;
    const where = categoryLabel ? ` ${C.inCategory} ${categoryLabel}` : '';
    return (
      <div className="gp-fd-empty">
        <p className="gp-fd-empty-title">{`${lead}${where}.`}</p>
        <Button onClick={clear}>{C.clearSearch}</Button>
      </div>
    );
  }
  return (
    // role="list": Safari/VoiceOver drops list semantics once list-style is none.
    <ul className="gp-fd-grid" role="list" aria-labelledby={GRID_TITLE_ID}>
      {shown.map((app) => (
        <li key={app.appId}>
          <AppTile app={app} />
        </li>
      ))}
      {!query.trim() && (
        <li className="gp-fd-grid-tail">
          <NextAppTile />
        </li>
      )}
    </ul>
  );
}

const QUICK_LAUNCH_LIMIT = 3;

/**
 * Right-hand Quick Launch dock in the hero band: the user's top three most-opened
 * apps and tools in this browser, falling back to a pointer to the tabs below
 * before they've opened any. It waits for the app list, since app history can only
 * be matched against it.
 */
function HeroQuickDock({ apps, loading }: { apps: readonly PluginEntry[]; loading: boolean }) {
  const usage = useLocalUsage();
  const items = loading ? [] : topQuickLaunchItems(apps, TOOLS, usage, QUICK_LAUNCH_LIMIT);
  return (
    <aside className="gp-fd-dock" aria-label={C.dockLabel} aria-busy={loading}>
      <CornerTicks />
      <p className="gp-fd-dock-kicker" aria-hidden="true">
        {C.dockKicker}
      </p>
      {loading ? (
        <div className="gp-fd-dock-grid" aria-hidden="true">
          {Array.from({ length: QUICK_LAUNCH_LIMIT }, (_, i) => (
            <span key={i} className="gp-fd-dock-placeholder" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="gp-fd-dock-empty">{C.dockEmpty}</p>
      ) : (
        <div className="gp-fd-dock-grid">
          {items.map((item) => (
            <QuickLaunchLink key={item.target} item={item} />
          ))}
        </div>
      )}
    </aside>
  );
}

/**
 * One Quick Launch entry, a real link like the tiles: a hosted app routes in the
 * shell (RemoteRoute counts the open when it mounts), and a linked app or a tool
 * opens a new tab and is counted on the click.
 */
function QuickLaunchLink({ item }: { item: QuickLaunchItem }) {
  const track = useTrackClick();
  const mark = (item.kind === 'app' ? appIcon(item.id) : undefined) ?? monogram(item.name);
  const body = (
    <>
      <span className="gp-fd-mark gp-fd-dock-mark" aria-hidden="true">
        {mark}
      </span>
      <span className="gp-fd-dock-name">{item.name}</span>
      {item.external && <span className="gp-fd-sr-only">, {C.dockNewTab}</span>}
      <span className="gp-fd-dock-arrow" aria-hidden="true">
        {item.external ? '↗' : '→'}
      </span>
    </>
  );
  const style = categoryStyle(item.category);
  if (item.external) {
    return (
      <a
        className="gp-fd-dock-item"
        href={item.href}
        target="_blank"
        rel="noopener noreferrer"
        style={style}
        onClick={() => track(item.target)}
      >
        {body}
      </a>
    );
  }
  return (
    <FdLink to={item.href} className="gp-fd-dock-item" style={style}>
      {body}
    </FdLink>
  );
}
